import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useAdminNotifications } from "@/hooks/useAdminNotifications";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { 
  Loader2, 
  LogOut, 
  Users, 
  Mail, 
  Calendar, 
  Search,
  Trash2,
  AlertCircle,
  ArrowLeft,
  Wrench,
  AlertTriangle,
  CheckCircle,
  Clock,
  XCircle,
  Send,
  RotateCcw,
  Bell,
  BadgeCheck,
  Download,
  TrendingUp,
  UserPlus,
  UserCheck,
  BarChart3,
  Filter,
  MessageCircle,
  Brain,
  FileText,
  Activity
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { format, toZonedTime } from "date-fns-tz";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserDetailModal } from "@/components/admin/UserDetailModal";
import { SupportChatPanel } from "@/components/admin/SupportChatPanel";
import { SupportAnalytics } from "@/components/admin/SupportAnalytics";
import { BotTraining } from "@/components/admin/BotTraining";
import { ReportSettings } from "@/components/admin/ReportSettings";
import ErrorTrackingPanel from "@/components/admin/ErrorTrackingPanel";

interface Lead {
  id: string;
  first_name: string;
  email: string;
  source: string | null;
  country: string | null;
  created_at: string;
  is_verified?: boolean;
}

interface ToolUsage {
  id: string;
  tool_id: string;
  tool_name: string;
  accessed_at: string;
  email?: string;
  full_name?: string;
}

interface IssueReport {
  id: string;
  email: string;
  full_name: string | null;
  tool_id: string | null;
  tool_name: string | null;
  issue_type: string;
  description: string;
  status: string;
  admin_notes: string | null;
  created_at: string;
}

interface EmailStats {
  email: string;
  full_name: string;
  total_emails_sent: number;
  tools_run_count: number;
  last_activity: string;
  is_verified: boolean;
  tools_used: string[];
}

type DateRangeFilter = 'all' | '7d' | '30d' | '90d';

interface LimitResetRequest {
  id: string;
  email: string;
  full_name: string;
  reason: string;
  status: string;
  created_at: string;
}

// Admin emails to exclude from all analytics and tables
const EXCLUDED_ADMIN_EMAILS = ['aman.sajid@itdukes.com', 'ahsan.sajid@itdukes.com', 'aman@coldiq.com'];

const Admin = () => {
  const { user, loading, isAdmin, signOut } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [leads, setLeads] = useState<Lead[]>([]);
  const [verifiedEmails, setVerifiedEmails] = useState<Set<string>>(new Set());
  const [toolUsage, setToolUsage] = useState<ToolUsage[]>([]);
  const [issueReports, setIssueReports] = useState<IssueReport[]>([]);
  const [emailStats, setEmailStats] = useState<EmailStats[]>([]);
  const [loadingLeads, setLoadingLeads] = useState(true);
  const [loadingUsage, setLoadingUsage] = useState(true);
  const [loadingIssues, setLoadingIssues] = useState(true);
  const [loadingEmailStats, setLoadingEmailStats] = useState(true);
  const [limitRequests, setLimitRequests] = useState<LimitResetRequest[]>([]);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [updatingIssueId, setUpdatingIssueId] = useState<string | null>(null);
  const [resettingEmail, setResettingEmail] = useState<string | null>(null);
  const [processingRequestId, setProcessingRequestId] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<DateRangeFilter>('all');
  const [selectedUserEmail, setSelectedUserEmail] = useState<string | null>(null);
  const [selectedUserName, setSelectedUserName] = useState<string>("");
  const [supportNeedsHumanCount, setSupportNeedsHumanCount] = useState(0);

  // Use admin notifications hook for sound/browser alerts
  const { requestNotificationPermission, hasNotificationPermission, notificationsDenied, notificationsSupported } = useAdminNotifications(supportNeedsHumanCount);

  // Fetch support conversations count that need human attention
  useEffect(() => {
    const fetchSupportCount = async () => {
      const { data, error } = await supabase
        .from("support_conversations")
        .select("id", { count: 'exact' })
        .eq("requires_human", true)
        .neq("status", "closed");
      
      if (!error) {
        setSupportNeedsHumanCount(data?.length || 0);
      }
    };

    if (user && isAdmin) {
      fetchSupportCount();
      
      // Set up realtime subscription
      const channel = supabase
        .channel('support-count')
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'support_conversations' },
          () => fetchSupportCount()
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [user, isAdmin]);

  // Compute filtered data excluding admin emails
  const filteredLeads = useMemo(() => 
    leads.filter(l => !EXCLUDED_ADMIN_EMAILS.includes(l.email.toLowerCase())), 
    [leads]
  );
  const filteredToolUsage = useMemo(() => 
    toolUsage.filter(u => !EXCLUDED_ADMIN_EMAILS.includes(u.email?.toLowerCase() || '')), 
    [toolUsage]
  );
  const filteredEmailStats = useMemo(() => 
    emailStats.filter(s => !EXCLUDED_ADMIN_EMAILS.includes(s.email.toLowerCase())), 
    [emailStats]
  );

  // Date filtered data
  const getDateFilteredData = useMemo(() => {
    const now = new Date();
    let cutoffDate: Date | null = null;
    
    switch (dateRange) {
      case '7d':
        cutoffDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case '30d':
        cutoffDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case '90d':
        cutoffDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        break;
      default:
        cutoffDate = null;
    }

    return {
      leads: cutoffDate 
        ? filteredLeads.filter(l => new Date(l.created_at) >= cutoffDate!) 
        : filteredLeads,
      toolUsage: cutoffDate 
        ? filteredToolUsage.filter(u => new Date(u.accessed_at) >= cutoffDate!) 
        : filteredToolUsage,
      emailStats: cutoffDate 
        ? filteredEmailStats.filter(s => new Date(s.last_activity) >= cutoffDate!) 
        : filteredEmailStats,
    };
  }, [dateRange, filteredLeads, filteredToolUsage, filteredEmailStats]);

  useEffect(() => {
    if (!loading && !user) {
      navigate("/auth");
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user && isAdmin) {
      fetchVerifiedEmails();
      fetchLeads();
      fetchToolUsage();
      fetchIssueReports();
      fetchEmailStats();
      fetchLimitRequests();
    }
  }, [user, isAdmin]);

  const fetchVerifiedEmails = async () => {
    const { data } = await supabase
      .from("tool_access")
      .select("email")
      .not("verified_at", "is", null);
    
    if (data) {
      setVerifiedEmails(new Set(data.map(d => d.email)));
    }
  };

  const fetchLeads = async () => {
    setLoadingLeads(true);
    const { data, error } = await supabase
      .from("leads")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching leads:", error);
      toast({
        title: "Error loading leads",
        description: error.message,
        variant: "destructive",
      });
    } else {
      setLeads(data || []);
    }
    setLoadingLeads(false);
  };

  const fetchToolUsage = async () => {
    setLoadingUsage(true);
    const { data, error } = await supabase
      .from("tool_usage")
      .select(`
        id,
        tool_id,
        tool_name,
        accessed_at,
        tool_access:tool_access_id (
          email,
          full_name
        )
      `)
      .order("accessed_at", { ascending: false })
      .limit(100);

    if (error) {
      console.error("Error fetching tool usage:", error);
    } else {
      const formattedData = (data || []).map((item: any) => ({
        id: item.id,
        tool_id: item.tool_id,
        tool_name: item.tool_name,
        accessed_at: item.accessed_at,
        email: item.tool_access?.email,
        full_name: item.tool_access?.full_name,
      }));
      setToolUsage(formattedData);
    }
    setLoadingUsage(false);
  };

  const fetchIssueReports = async () => {
    setLoadingIssues(true);
    const { data, error } = await supabase
      .from("issue_reports")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching issues:", error);
    } else {
      setIssueReports(data || []);
    }
    setLoadingIssues(false);
  };

  const fetchEmailStats = async () => {
    setLoadingEmailStats(true);
    
    // Fetch tool access data
    const { data: accessData, error: accessError } = await supabase
      .from("tool_access")
      .select("id, email, full_name, emails_sent_count, updated_at, verified_at")
      .order("updated_at", { ascending: false });

    // Fetch tool usage to get tools used per user
    const { data: usageData } = await supabase
      .from("tool_usage")
      .select("tool_access_id, tool_name");

    if (accessError) {
      console.error("Error fetching email stats:", accessError);
    } else {
      // Create maps of tool_access_id to tools used and run counts
      const toolsPerUser: Record<string, Set<string>> = {};
      const toolRunsPerUser: Record<string, number> = {};
      (usageData || []).forEach((usage) => {
        if (usage.tool_access_id) {
          if (!toolsPerUser[usage.tool_access_id]) {
            toolsPerUser[usage.tool_access_id] = new Set();
          }
          toolsPerUser[usage.tool_access_id].add(usage.tool_name);
          toolRunsPerUser[usage.tool_access_id] = (toolRunsPerUser[usage.tool_access_id] || 0) + 1;
        }
      });

      // Aggregate by email
      const aggregated: Record<string, EmailStats & { accessIds: string[] }> = {};
      (accessData || []).forEach((record) => {
        if (!aggregated[record.email]) {
          aggregated[record.email] = {
            email: record.email,
            full_name: record.full_name,
            total_emails_sent: 0,
            tools_run_count: 0,
            last_activity: record.updated_at,
            is_verified: false,
            tools_used: [],
            accessIds: [],
          };
        }
        aggregated[record.email].accessIds.push(record.id);
        aggregated[record.email].total_emails_sent += record.emails_sent_count || 0;
        if (record.verified_at) {
          aggregated[record.email].is_verified = true;
        }
        if (new Date(record.updated_at) > new Date(aggregated[record.email].last_activity)) {
          aggregated[record.email].last_activity = record.updated_at;
        }
      });

      // Collect unique tools and total run count for each email
      Object.values(aggregated).forEach((stat) => {
        const toolsSet = new Set<string>();
        let totalRuns = 0;
        stat.accessIds.forEach((id) => {
          const tools = toolsPerUser[id];
          if (tools) {
            tools.forEach((t) => toolsSet.add(t));
          }
          totalRuns += toolRunsPerUser[id] || 0;
        });
        stat.tools_used = Array.from(toolsSet).sort();
        stat.tools_run_count = totalRuns;
      });
      
      // Sort by tools run count descending, remove accessIds from final result
      const sorted = Object.values(aggregated)
        .map(({ accessIds, ...rest }) => rest)
        .sort((a, b) => b.tools_run_count - a.tools_run_count);
      setEmailStats(sorted);
    }
    setLoadingEmailStats(false);
  };

  const fetchLimitRequests = async () => {
    setLoadingRequests(true);
    const { data, error } = await supabase
      .from("limit_reset_requests")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching limit requests:", error);
    } else {
      setLimitRequests(data || []);
    }
    setLoadingRequests(false);
  };

  const handleResetEmailLimit = async (email: string) => {
    setResettingEmail(email);
    try {
      const { data, error } = await supabase.functions.invoke("admin-actions", {
        body: { action: "reset_email_limit", email },
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);

      toast({
        title: "Limit reset",
        description: `Email limit reset for ${email}`,
      });

      // Refresh email stats
      fetchEmailStats();
    } catch (error: any) {
      toast({
        title: "Error resetting limit",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setResettingEmail(null);
    }
  };

  const handleLimitRequest = async (requestId: string, action: "approve" | "deny") => {
    setProcessingRequestId(requestId);
    try {
      if (action === "approve") {
        const { data, error } = await supabase.functions.invoke("admin-actions", {
          body: { action: "approve_limit_request", requestId },
        });

        if (error) throw error;
        if (data.error) throw new Error(data.error);

        toast({
          title: "Request approved",
          description: "The user's email limit has been reset.",
        });
      } else {
        // Deny - just update status
        await supabase
          .from("limit_reset_requests")
          .update({ status: "denied", reviewed_at: new Date().toISOString() })
          .eq("id", requestId);

        toast({
          title: "Request denied",
          description: "The request has been denied.",
        });
      }

      // Refresh both
      fetchLimitRequests();
      fetchEmailStats();
    } catch (error: any) {
      toast({
        title: "Error processing request",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setProcessingRequestId(null);
    }
  };

  const exportLeadsCSV = () => {
    const headers = ["Name", "Email", "Source", "Country", "Verified", "Date"];
    const rows = leads.map(lead => [
      lead.first_name,
      lead.email,
      lead.source || "",
      lead.country || "",
      verifiedEmails.has(lead.email) ? "Yes" : "No",
      format(toZonedTime(new Date(lead.created_at), 'America/New_York'), "yyyy-MM-dd HH:mm:ss")
    ]);
    
    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `leads_${format(new Date(), "yyyy-MM-dd")}.csv`;
    link.click();
    
    toast({ title: "Export complete", description: `Exported ${leads.length} leads` });
  };

  const exportEmailStatsCSV = () => {
    const headers = ["Name", "Email", "Tools Run", "Tools Used", "Results Emailed", "Verified", "Last Activity"];
    const rows = emailStats.map(stat => [
      stat.full_name,
      stat.email,
      stat.tools_run_count,
      stat.tools_used.join("; "),
      stat.total_emails_sent,
      stat.is_verified ? "Yes" : "No",
      format(toZonedTime(new Date(stat.last_activity), 'America/New_York'), "yyyy-MM-dd HH:mm:ss")
    ]);
    
    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `user_activity_${format(new Date(), "yyyy-MM-dd")}.csv`;
    link.click();
    
    toast({ title: "Export complete", description: `Exported ${emailStats.length} records` });
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    const { error } = await supabase.from("leads").delete().eq("id", id);
    
    if (error) {
      toast({
        title: "Error deleting lead",
        description: error.message,
        variant: "destructive",
      });
    } else {
      setLeads(leads.filter((lead) => lead.id !== id));
      toast({
        title: "Lead deleted",
        description: "The lead has been removed.",
      });
    }
    setDeletingId(null);
  };

  const updateIssueStatus = async (id: string, newStatus: string) => {
    setUpdatingIssueId(id);
    const { error } = await supabase
      .from("issue_reports")
      .update({ status: newStatus })
      .eq("id", id);
    
    if (error) {
      toast({
        title: "Error updating issue",
        description: error.message,
        variant: "destructive",
      });
    } else {
      setIssueReports(issueReports.map(issue => 
        issue.id === id ? { ...issue, status: newStatus } : issue
      ));
      toast({
        title: "Issue updated",
        description: `Status changed to ${newStatus}`,
      });
    }
    setUpdatingIssueId(null);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/auth");
  };

  const searchFilteredLeads = filteredLeads.filter(
    (lead) =>
      lead.first_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getIssueStatusConfig = (status: string) => {
    switch (status) {
      case "resolved":
        return { icon: <CheckCircle className="w-4 h-4" />, color: "text-success", bg: "bg-success/10" };
      case "in-progress":
        return { icon: <Clock className="w-4 h-4" />, color: "text-warning", bg: "bg-warning/10" };
      case "closed":
        return { icon: <XCircle className="w-4 h-4" />, color: "text-muted-foreground", bg: "bg-muted" };
      default:
        return { icon: <AlertTriangle className="w-4 h-4" />, color: "text-destructive", bg: "bg-destructive/10" };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-destructive mx-auto mb-4" />
          <h1 className="font-display text-2xl font-bold text-foreground mb-2">
            Access Denied
          </h1>
          <p className="text-muted-foreground mb-6">
            You don't have admin privileges to view this page.
          </p>
          <button
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Users className="w-6 h-6 text-primary" />
            <h1 className="font-display text-xl font-bold text-foreground">
              Admin Dashboard
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground hidden sm:block">
              {user?.email}
            </span>
            <button
              onClick={handleSignOut}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Sign Out</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Date Range Filter */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-foreground">Dashboard Overview</h2>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <Select value={dateRange} onValueChange={(value: DateRangeFilter) => setDateRange(value)}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Date range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Time</SelectItem>
                <SelectItem value="7d">Last 7 Days</SelectItem>
                <SelectItem value="30d">Last 30 Days</SelectItem>
                <SelectItem value="90d">Last 90 Days</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-8">
          <div className="bg-card rounded-xl border border-border p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Users className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Leads</p>
                <p className="text-2xl font-bold text-foreground">{getDateFilteredData.leads.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-card rounded-xl border border-border p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                <Mail className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">This Week</p>
                <p className="text-2xl font-bold text-foreground">
                  {filteredLeads.filter((l) => {
                    const weekAgo = new Date();
                    weekAgo.setDate(weekAgo.getDate() - 7);
                    return new Date(l.created_at) > weekAgo;
                  }).length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-card rounded-xl border border-border p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Wrench className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Tool Uses</p>
                <p className="text-2xl font-bold text-foreground">{getDateFilteredData.toolUsage.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-card rounded-xl border border-border p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-violet-500/10 flex items-center justify-center">
                <Activity className="w-5 h-5 text-violet-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Results Emailed</p>
                <p className="text-2xl font-bold text-foreground">
                  {filteredEmailStats.reduce((sum, s) => sum + s.total_emails_sent, 0)}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-card rounded-xl border border-border p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Open Issues</p>
                <p className="text-2xl font-bold text-foreground">
                  {issueReports.filter(i => i.status === "open").length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Notification Permission Banner */}
        {notificationsSupported && !hasNotificationPermission && !notificationsDenied && (
          <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Bell className="w-5 h-5 text-primary" />
              <div>
                <p className="font-medium text-foreground">Enable browser notifications</p>
                <p className="text-sm text-muted-foreground">Get instant alerts when users request human help</p>
              </div>
            </div>
            <Button onClick={requestNotificationPermission} size="sm">
              Enable Notifications
            </Button>
          </div>
        )}

        {notificationsDenied && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-destructive" />
            <div>
              <p className="font-medium text-foreground">Notifications blocked</p>
              <p className="text-sm text-muted-foreground">Please enable notifications in your browser settings to receive support alerts</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="analytics" className="space-y-6">
          <TabsList className="flex-wrap">
            <TabsTrigger value="analytics" className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Analytics
            </TabsTrigger>
            <TabsTrigger value="leads" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Leads ({filteredLeads.length})
            </TabsTrigger>
            <TabsTrigger value="usage" className="flex items-center gap-2">
              <Wrench className="w-4 h-4" />
              Tool Usage ({filteredToolUsage.length})
            </TabsTrigger>
            <TabsTrigger value="issues" className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Issues ({issueReports.filter(i => i.status === "open").length})
            </TabsTrigger>
            <TabsTrigger value="email-stats" className="flex items-center gap-2">
              <Activity className="w-4 h-4" />
              User Activity ({filteredEmailStats.length})
            </TabsTrigger>
            <TabsTrigger value="requests" className="flex items-center gap-2">
              <Bell className="w-4 h-4" />
              Requests ({limitRequests.filter(r => r.status === "pending").length})
            </TabsTrigger>
            <TabsTrigger value="support" className="flex items-center gap-2 relative">
              <MessageCircle className="w-4 h-4" />
              Support
              {supportNeedsHumanCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold bg-destructive text-destructive-foreground rounded-full px-1 animate-pulse">
                  {supportNeedsHumanCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="bot-training" className="flex items-center gap-2">
              <Brain className="w-4 h-4" />
              Bot Training
            </TabsTrigger>
            <TabsTrigger value="reports" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Reports
            </TabsTrigger>
            <TabsTrigger value="errors" className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              Error Tracking
            </TabsTrigger>
          </TabsList>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="space-y-6">
            {(() => {
              // Use the pre-filtered data
              const analyticsLeads = getDateFilteredData.leads;
              const analyticsEmailStats = getDateFilteredData.emailStats;
              const analyticsToolUsage = getDateFilteredData.toolUsage;

              // Calculate analytics
              const now = new Date();
              const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
              const weekAgo = new Date(today);
              weekAgo.setDate(weekAgo.getDate() - 7);
              const monthAgo = new Date(today);
              monthAgo.setMonth(monthAgo.getMonth() - 1);

              // Leads analytics
              const newLeadsToday = analyticsLeads.filter(l => new Date(l.created_at) >= today).length;
              const newLeadsThisWeek = analyticsLeads.filter(l => new Date(l.created_at) >= weekAgo).length;
              const newLeadsThisMonth = analyticsLeads.filter(l => new Date(l.created_at) >= monthAgo).length;

              // Active users (users with tool runs > 0)
              const activeUsers = analyticsEmailStats.filter(s => s.tools_run_count > 0);
              const activeRate = analyticsEmailStats.length > 0 
                ? Math.round((activeUsers.length / analyticsEmailStats.length) * 100) 
                : 0;

              // Verified vs unverified
              const verifiedCount = analyticsEmailStats.filter(s => s.is_verified).length;
              const verificationRate = analyticsEmailStats.length > 0 
                ? Math.round((verifiedCount / analyticsEmailStats.length) * 100) 
                : 0;

              // Tool usage by day
              const toolUsageToday = analyticsToolUsage.filter(u => new Date(u.accessed_at) >= today).length;
              const toolUsageThisWeek = analyticsToolUsage.filter(u => new Date(u.accessed_at) >= weekAgo).length;

              // Most popular tools
              const toolCounts: Record<string, number> = {};
              analyticsToolUsage.forEach(u => {
                toolCounts[u.tool_name] = (toolCounts[u.tool_name] || 0) + 1;
              });
              const topTools = Object.entries(toolCounts)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 5);

              // Most active users
              const topUsers = analyticsEmailStats
                .filter(s => s.tools_run_count > 0)
                .sort((a, b) => b.tools_run_count - a.tools_run_count)
                .slice(0, 5);

              return (
                <>
                  {/* User Engagement Overview */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-card rounded-xl border border-border p-5">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                          <UserPlus className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Active Users</p>
                          <p className="text-2xl font-bold text-foreground">{activeUsers.length}</p>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">{activeRate}% have used tools</p>
                    </div>
                    
                    <div className="bg-card rounded-xl border border-border p-5">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                          <UserCheck className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Inactive Users</p>
                          <p className="text-2xl font-bold text-foreground">{analyticsEmailStats.length - activeUsers.length}</p>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">Registered but no tool usage</p>
                    </div>
                    
                    <div className="bg-card rounded-xl border border-border p-5">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-lg bg-violet-500/10 flex items-center justify-center">
                          <BadgeCheck className="w-5 h-5 text-violet-600" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Verified Users</p>
                          <p className="text-2xl font-bold text-foreground">{verifiedCount}</p>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">{verificationRate}% verification rate</p>
                    </div>
                    
                    <div className="bg-card rounded-xl border border-border p-5">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                          <TrendingUp className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Tool Uses Today</p>
                          <p className="text-2xl font-bold text-foreground">{toolUsageToday}</p>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">{toolUsageThisWeek} this week</p>
                    </div>
                  </div>

                  {/* Lead Growth */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-card rounded-xl border border-border p-6">
                      <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                        <Calendar className="w-5 h-5 text-primary" />
                        Lead Growth
                      </h3>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">Today</span>
                          <span className="font-semibold text-foreground">{newLeadsToday} leads</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">This Week</span>
                          <span className="font-semibold text-foreground">{newLeadsThisWeek} leads</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">This Month</span>
                          <span className="font-semibold text-foreground">{newLeadsThisMonth} leads</span>
                        </div>
                        <div className="flex items-center justify-between pt-3 border-t border-border">
                          <span className="text-muted-foreground">All Time</span>
                          <span className="font-bold text-foreground">{analyticsLeads.length} leads</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-card rounded-xl border border-border p-6">
                      <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                        <Wrench className="w-5 h-5 text-primary" />
                        Most Popular Tools
                      </h3>
                      {topTools.length > 0 ? (
                        <div className="space-y-3">
                          {topTools.map(([tool, count], index) => (
                            <div key={tool} className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-xs font-medium">
                                  {index + 1}
                                </span>
                                <span className="text-foreground text-sm truncate max-w-[180px]">{tool}</span>
                              </div>
                              <span className="text-muted-foreground text-sm">{count} uses</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-muted-foreground text-sm">No tool usage recorded yet</p>
                      )}
                    </div>
                  </div>

                  {/* Most Active Returning Users */}
                  <div className="bg-card rounded-xl border border-border p-6">
                    <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                      <UserCheck className="w-5 h-5 text-primary" />
                      Most Active Returning Users
                    </h3>
                    {topUsers.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="border-b border-border">
                            <tr>
                              <th className="text-left text-sm font-medium text-muted-foreground px-3 py-2">User</th>
                              <th className="text-left text-sm font-medium text-muted-foreground px-3 py-2">Email</th>
                              <th className="text-center text-sm font-medium text-muted-foreground px-3 py-2">Tools Run</th>
                              <th className="text-left text-sm font-medium text-muted-foreground px-3 py-2">Last Active</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-border">
                            {topUsers.map((user) => (
                              <tr key={user.email} className="hover:bg-muted/30 transition-colors">
                                <td className="px-3 py-3 text-foreground font-medium">
                                  <div className="flex items-center gap-2">
                                    {user.full_name}
                                    {user.is_verified && (
                                      <BadgeCheck className="w-4 h-4 text-success" />
                                    )}
                                  </div>
                                </td>
                                <td className="px-3 py-3 text-muted-foreground text-sm">{user.email}</td>
                                <td className="px-3 py-3 text-center">
                                  <span className="inline-flex px-2 py-1 rounded-lg text-xs font-medium bg-primary/10 text-primary">
                                    {user.tools_run_count}
                                  </span>
                                </td>
                                <td className="px-3 py-3 text-muted-foreground text-sm">
                                  {format(toZonedTime(new Date(user.last_activity), 'America/New_York'), "MMM d, h:mm a")}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-sm">No returning users yet</p>
                    )}
                  </div>
                </>
              );
            })()}
          </TabsContent>

          {/* Leads Tab */}
          <TabsContent value="leads" className="space-y-4">
            {/* Search and Export */}
            <div className="flex flex-col sm:flex-row gap-4 justify-between">
              <div className="relative max-w-md flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by name or email..."
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={exportLeadsCSV}
                disabled={leads.length === 0}
                className="gap-2"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </Button>
            </div>

            {/* Leads Table */}
            <div className="bg-card rounded-xl border border-border overflow-hidden">
              {loadingLeads ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : searchFilteredLeads.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">
                    {searchQuery ? "No leads match your search" : "No leads captured yet"}
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/50 border-b border-border">
                      <tr>
                        <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3">Name</th>
                        <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3">Email</th>
                        <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3 hidden sm:table-cell">Source</th>
                        <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3 hidden lg:table-cell">Country</th>
                        <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3 hidden md:table-cell">Date</th>
                        <th className="text-right text-sm font-medium text-muted-foreground px-4 py-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {searchFilteredLeads.map((lead) => {
                        const isVerified = verifiedEmails.has(lead.email);
                        return (
                          <tr key={lead.id} className="hover:bg-muted/30 transition-colors">
                            <td className="px-4 py-3 text-foreground font-medium">
                              <div className="flex items-center gap-2">
                                {lead.first_name}
                                {isVerified && (
                                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-success/10 text-success" title="OTP Verified">
                                    <BadgeCheck className="w-3 h-3" />
                                    Verified
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-muted-foreground">{lead.email}</td>
                          <td className="px-4 py-3 text-muted-foreground hidden sm:table-cell">
                            <span className="inline-flex px-2 py-1 rounded-full text-xs bg-muted">
                              {lead.source || "N/A"}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-muted-foreground hidden lg:table-cell">
                            {lead.country || "—"}
                          </td>
                          <td className="px-4 py-3 text-muted-foreground hidden md:table-cell">
                            {format(toZonedTime(new Date(lead.created_at), 'America/New_York'), "MMM d, yyyy h:mm a")} EST
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              onClick={() => handleDelete(lead.id)}
                              disabled={deletingId === lead.id}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-sm text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-50"
                            >
                              {deletingId === lead.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Trash2 className="w-4 h-4" />
                              )}
                            </button>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Tool Usage Tab */}
          <TabsContent value="usage" className="space-y-4">
            {(() => {
              // Filter out admin emails from tool usage display
              const filteredToolUsage = toolUsage.filter(u => !EXCLUDED_ADMIN_EMAILS.includes(u.email?.toLowerCase() || ''));
              
              return (
                <div className="bg-card rounded-xl border border-border overflow-hidden">
                  {loadingUsage ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-primary" />
                    </div>
                  ) : filteredToolUsage.length === 0 ? (
                    <div className="text-center py-12">
                      <Wrench className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                      <p className="text-muted-foreground">No tool usage recorded yet</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-muted/50 border-b border-border">
                          <tr>
                            <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3">Tool</th>
                            <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3">User</th>
                            <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3 hidden sm:table-cell">Email</th>
                            <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3">Date</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {filteredToolUsage.map((usage) => (
                            <tr key={usage.id} className="hover:bg-muted/30 transition-colors">
                              <td className="px-4 py-3">
                                <span className="inline-flex px-2 py-1 rounded-lg text-xs font-medium bg-primary/10 text-primary">
                                  {usage.tool_name}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-foreground">{usage.full_name || "—"}</td>
                              <td className="px-4 py-3 text-muted-foreground hidden sm:table-cell">{usage.email || "—"}</td>
                              <td className="px-4 py-3 text-muted-foreground text-sm">
                                {format(toZonedTime(new Date(usage.accessed_at), 'America/New_York'), "MMM d, h:mm a")}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })()}
          </TabsContent>

          {/* Issues Tab */}
          <TabsContent value="issues" className="space-y-4">
            <div className="bg-card rounded-xl border border-border overflow-hidden">
              {loadingIssues ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : issueReports.length === 0 ? (
                <div className="text-center py-12">
                  <AlertTriangle className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">No issues reported yet</p>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {issueReports.map((issue) => {
                    const statusConfig = getIssueStatusConfig(issue.status);
                    return (
                      <div key={issue.id} className="p-4 hover:bg-muted/30 transition-colors">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.color}`}>
                                {statusConfig.icon}
                                {issue.status}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {issue.issue_type}
                              </span>
                              {issue.tool_name && (
                                <span className="inline-flex px-2 py-1 rounded-lg text-xs bg-muted">
                                  {issue.tool_name}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-foreground mb-2">{issue.description}</p>
                            <div className="flex items-center gap-4 text-xs text-muted-foreground">
                              <span>{issue.email}</span>
                              <span>•</span>
                              <span>{format(toZonedTime(new Date(issue.created_at), 'America/New_York'), "MMM d, yyyy h:mm a")}</span>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            {issue.status === "open" && (
                              <>
                                <button
                                  onClick={() => updateIssueStatus(issue.id, "in-progress")}
                                  disabled={updatingIssueId === issue.id}
                                  className="px-3 py-1.5 text-xs rounded-lg bg-warning/10 text-warning hover:bg-warning/20 transition-colors"
                                >
                                  In Progress
                                </button>
                                <button
                                  onClick={() => updateIssueStatus(issue.id, "resolved")}
                                  disabled={updatingIssueId === issue.id}
                                  className="px-3 py-1.5 text-xs rounded-lg bg-success/10 text-success hover:bg-success/20 transition-colors"
                                >
                                  Resolve
                                </button>
                              </>
                            )}
                            {issue.status === "in-progress" && (
                              <button
                                onClick={() => updateIssueStatus(issue.id, "resolved")}
                                disabled={updatingIssueId === issue.id}
                                className="px-3 py-1.5 text-xs rounded-lg bg-success/10 text-success hover:bg-success/20 transition-colors"
                              >
                                Resolve
                              </button>
                            )}
                            {issue.status !== "closed" && (
                              <button
                                onClick={() => updateIssueStatus(issue.id, "closed")}
                                disabled={updatingIssueId === issue.id}
                                className="px-3 py-1.5 text-xs rounded-lg bg-muted text-muted-foreground hover:bg-muted/80 transition-colors"
                              >
                                Close
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>

          {/* User Activity Tab */}
          <TabsContent value="email-stats" className="space-y-4">
            {(() => {
              const displayEmailStats = getDateFilteredData.emailStats;
              
              return (
                <>
                  {/* Summary stats at top */}
                  {displayEmailStats.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      <div className="bg-card rounded-xl border border-border p-4">
                        <p className="text-sm text-muted-foreground mb-1">Total Users</p>
                        <p className="text-xl font-bold text-foreground">{displayEmailStats.length}</p>
                      </div>
                      <div className="bg-card rounded-xl border border-border p-4">
                        <p className="text-sm text-muted-foreground mb-1">Verified Users</p>
                        <p className="text-xl font-bold text-foreground">
                          {displayEmailStats.filter(s => s.is_verified).length}
                        </p>
                      </div>
                      <div className="bg-card rounded-xl border border-border p-4">
                        <p className="text-sm text-muted-foreground mb-1">Total Tool Runs</p>
                        <p className="text-xl font-bold text-foreground">
                          {displayEmailStats.reduce((sum, s) => sum + s.tools_run_count, 0)}
                        </p>
                      </div>
                      <div className="bg-card rounded-xl border border-border p-4">
                        <p className="text-sm text-muted-foreground mb-1">Results Emailed</p>
                        <p className="text-xl font-bold text-foreground">
                          {displayEmailStats.reduce((sum, s) => sum + s.total_emails_sent, 0)}
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={exportEmailStatsCSV}
                      disabled={displayEmailStats.length === 0}
                      className="gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Export CSV
                    </Button>
                  </div>
                  <div className="bg-card rounded-xl border border-border overflow-hidden">
                    {loadingEmailStats ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-8 h-8 animate-spin text-primary" />
                      </div>
                    ) : displayEmailStats.length === 0 ? (
                      <div className="text-center py-12">
                        <Activity className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                        <p className="text-muted-foreground">No user activity recorded yet</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-muted/50 border-b border-border">
                            <tr>
                              <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3">User</th>
                              <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3">Email</th>
                              
                              <th className="text-center text-sm font-medium text-muted-foreground px-4 py-3">Tools Run</th>
                              <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3 hidden lg:table-cell">Tool Names</th>
                              <th className="text-center text-sm font-medium text-muted-foreground px-4 py-3" title="Number of times the user emailed tool results to themselves">Results Emailed</th>
                              <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3 hidden md:table-cell">Last Activity</th>
                              <th className="text-right text-sm font-medium text-muted-foreground px-4 py-3">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-border">
                            {displayEmailStats.map((stat) => (
                              <tr 
                                key={stat.email} 
                                className="hover:bg-muted/30 transition-colors cursor-pointer"
                                onClick={() => {
                                  setSelectedUserEmail(stat.email);
                                  setSelectedUserName(stat.full_name);
                                }}
                              >
                                <td className="px-4 py-3 text-foreground font-medium">
                                  <div className="flex items-center gap-2">
                                    {stat.full_name}
                                    {stat.is_verified && (
                                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-success/10 text-success" title="OTP Verified">
                                        <BadgeCheck className="w-3 h-3" />
                                        Verified
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-muted-foreground">{stat.email}</td>
                                <td className="px-4 py-3 text-center">
                                  <span className={`inline-flex px-2 py-1 rounded-lg text-xs font-medium ${
                                    stat.tools_run_count > 0 ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
                                  }`}>
                                    {stat.tools_run_count}
                                  </span>
                                </td>
                                <td className="px-4 py-3 hidden lg:table-cell">
                                  {stat.tools_used.length > 0 ? (
                                    <div className="flex flex-wrap gap-1 max-w-[250px]">
                                      {stat.tools_used.slice(0, 3).map((tool) => (
                                        <span key={tool} className="inline-flex px-1.5 py-0.5 rounded text-xs bg-primary/10 text-primary truncate max-w-[100px]" title={tool}>
                                          {tool}
                                        </span>
                                      ))}
                                      {stat.tools_used.length > 3 && (
                                        <span className="inline-flex px-1.5 py-0.5 rounded text-xs bg-muted text-muted-foreground" title={stat.tools_used.slice(3).join(', ')}>
                                          +{stat.tools_used.length - 3} more
                                        </span>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-xs text-muted-foreground">—</span>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <span className={`inline-flex px-2 py-1 rounded-lg text-xs font-medium ${
                                    stat.total_emails_sent >= 10
                                      ? "bg-destructive/10 text-destructive"
                                      : stat.total_emails_sent >= 8
                                        ? "bg-warning/10 text-warning"
                                        : "bg-muted text-muted-foreground"
                                  }`}>
                                    {stat.total_emails_sent} / 10
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-muted-foreground text-sm hidden md:table-cell">
                                  {format(toZonedTime(new Date(stat.last_activity), 'America/New_York'), "MMM d, h:mm a")}
                                </td>
                                <td className="px-4 py-3 text-right">
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleResetEmailLimit(stat.email); }}
                                    disabled={resettingEmail === stat.email || stat.total_emails_sent === 0}
                                    className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-sm text-primary hover:bg-primary/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    title="Reset results email limit"
                                  >
                                    {resettingEmail === stat.email ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <RotateCcw className="w-4 h-4" />
                                    )}
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </>
              );
            })()}
          </TabsContent>



          {/* Limit Reset Requests Tab */}
          <TabsContent value="requests" className="space-y-4">
            <div className="bg-card rounded-xl border border-border overflow-hidden">
              {loadingRequests ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : limitRequests.length === 0 ? (
                <div className="text-center py-12">
                  <Bell className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">No limit reset requests yet</p>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {limitRequests.map((request) => {
                    const isPending = request.status === "pending";
                    const statusConfig = request.status === "approved" 
                      ? { icon: <CheckCircle className="w-4 h-4" />, color: "text-success", bg: "bg-success/10" }
                      : request.status === "denied"
                        ? { icon: <XCircle className="w-4 h-4" />, color: "text-destructive", bg: "bg-destructive/10" }
                        : { icon: <Clock className="w-4 h-4" />, color: "text-warning", bg: "bg-warning/10" };
                    
                    return (
                      <div key={request.id} className="p-4 hover:bg-muted/30 transition-colors">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.color}`}>
                                {statusConfig.icon}
                                {request.status}
                              </span>
                              <span className="text-sm font-medium text-foreground">{request.full_name}</span>
                              <span className="text-sm text-muted-foreground">({request.email})</span>
                            </div>
                            <p className="text-sm text-muted-foreground mb-2">
                              <span className="font-medium">Reason:</span> {request.reason}
                            </p>
                            <div className="text-xs text-muted-foreground">
                              {format(toZonedTime(new Date(request.created_at), 'America/New_York'), "MMM d, yyyy h:mm a")}
                            </div>
                          </div>
                          {isPending && (
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleLimitRequest(request.id, "approve")}
                                disabled={processingRequestId === request.id}
                                className="px-3 py-1.5 text-xs rounded-lg bg-success/10 text-success hover:bg-success/20 transition-colors disabled:opacity-50"
                              >
                                {processingRequestId === request.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  "Approve"
                                )}
                              </button>
                              <button
                                onClick={() => handleLimitRequest(request.id, "deny")}
                                disabled={processingRequestId === request.id}
                                className="px-3 py-1.5 text-xs rounded-lg bg-destructive/10 text-destructive hover:bg-destructive/20 transition-colors disabled:opacity-50"
                              >
                                Deny
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Support Tab */}
          <TabsContent value="support" className="space-y-6">
            <SupportAnalytics />
            <SupportChatPanel />
          </TabsContent>

          {/* Bot Training Tab */}
          <TabsContent value="bot-training" className="space-y-6">
            <BotTraining />
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports" className="space-y-6">
            <ReportSettings />
          </TabsContent>

          {/* Error Tracking Tab */}
          <TabsContent value="errors" className="space-y-6">
            <ErrorTrackingPanel />
          </TabsContent>
        </Tabs>
      </main>

      {/* User Detail Modal */}
      <UserDetailModal
        isOpen={!!selectedUserEmail}
        onClose={() => setSelectedUserEmail(null)}
        email={selectedUserEmail || ""}
        fullName={selectedUserName}
      />
    </div>
  );
};

export default Admin;